import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  BatteryCharging,
  Droplets,
  Gauge,
  Thermometer,
  Zap,
} from "lucide-react";
import { io } from "socket.io-client";

import "./index.css";

const formatLabel = (key) =>
  key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const metricUnits = {
  dc_meter1_voltage: "V",
  dc_meter2_voltage: "V",
  phase1_voltage: "V",
  phase2_voltage: "V",
  phase3_voltage: "V",
  dc_meter1_current: "A",
  dc_meter2_current: "A",
  phase1_current: "A",
  phase2_current: "A",
  phase3_current: "A",
  dc_meter1_power: "kW",
  dc_meter2_power: "kW",
  phase1_power: "kW",
  phase2_power: "kW",
  phase3_power: "kW",
  total_dc_power: "kW",
  total_active_power: "kW",
  dc_meter1_energy_reading: "kWh",
  dc_meter2_energy_reading: "kWh",
  dc_meter1_energy_consumed: "kWh",
  dc_meter2_energy_consumed: "kWh",
  total_energy: "kWh",
  ac_meter_energyreading: "kWh",
  efficiency: "%",
  gun1_temp: "degC",
  gun2_temp: "degC",
  total_avg_temp: "degC",
  ambient_temperature: "degC",
  cabin_temperature: "degC",
};

const getStatusTone = (value) => {
  const normalized = String(value ?? "").toLowerCase();

  if (normalized.includes("state_c") || normalized.includes("active")) {
    return "is-active";
  }

  if (normalized.includes("state_f") || normalized.includes("fault")) {
    return "is-fault";
  }

  return "is-idle";
};

const LiquidCooledDetails = ({ stationId, dispensorId }) => {
  const [latestRecord, setLatestRecord] = useState({
    gun1_cp_state: "state_f",
    gun2_cp_state: "state_f",
    dc_meter1_voltage: 0.0,
    dc_meter1_current: 0.0,
    dc_meter1_power: 0.0,
    dc_meter1_energy_reading: 0.0,
    dc_meter1_energy_consumed: 0,
    dc_meter2_voltage: 0.0,
    dc_meter2_current: 0.0,
    dc_meter2_power: 0.0,
    dc_meter2_energy_reading: 0,
    dc_meter2_energy_consumed: 0,
    total_dc_power: 0.0,
    total_energy: 0.0,
    total_active_power: 0,
    efficiency: 0.0,
    phase1_voltage: 0,
    phase1_current: 0,
    phase1_power: 0,
    phase2_voltage: 0,
    phase2_current: 0,
    phase2_power: 0,
    phase3_voltage: 0,
    phase3_current: 0,
    phase3_power: 0,
    ambient_temperature: 0,
    cabin_temperature: 0,
    ac_meter_energyreading: 0,
    gun1_temperature: 0,
    gun2_temperature: 0,
    guns_avg_temp: 0,
  });

  const socketRef = useRef(null);
  useEffect(() => {
    if (!socketRef.current) {
      const socket = io(
        `${process.env.REACT_APP_IP_ADDRESS || ""}/liquid_cool_records`,
        {
          transports: ["websocket","polling"],
          reconnection: true,
        },
      );

      socketRef.current = socket;

      socket.on("connect", () => {
        console.log("Connected to /liquid_cool_records");

        socket.emit("get_dispensor_data", {
          station_id: stationId,
          dispensor_id: dispensorId,
        });
      });

      socket.on("dispensor_data", (data) => {
        console.log("Dispensor data from liquid cool:", data);

        if (data?.data) {
          const normalizedUpdates = Object.fromEntries(
            Object.entries(data.data).map(([key, value]) => [
              key,
              Number.isNaN(Number(value)) ? value : Number(value),
            ]),
          );

          setLatestRecord((prev) => ({
            ...prev,
            ...normalizedUpdates,
            _updatedAt: Date.now(),
          }));
        }
      });

      socket.on("disconnect", () => {
        console.warn("Disconnected from /liquid_cool_records");
      });
    }

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [stationId, dispensorId]);

  const formatValue = (value, key) => {
    if (typeof value !== "number") {
      return String(value ?? "--").replace(/_/g, " ").toUpperCase();
    }

    const formatted = Number.isInteger(value) ? value : value.toFixed(2);
    return metricUnits[key] ? `${formatted} ${metricUnits[key]}` : formatted;
  };

  const summaryMetrics = useMemo(
    () => [
      {
        label: "Total DC Power",
        value: latestRecord.total_dc_power,
        key: "total_dc_power",
        tone: "power",
      },
      {
        label: "Efficiency",
        value: latestRecord.efficiency,
        key: "efficiency",
        tone: "efficiency",
      },
      {
        label: "Avg Gun Temp",
        value: latestRecord.guns_avg_temp,
        key: "total_avg_temp",
        tone: "temperature",
      },
      {
        label: "Total Energy",
        value: latestRecord.total_energy,
        key: "total_energy",
        tone: "energy",
      },
    ],
    [latestRecord],
  );

  const categories = [
    {
      title: "Gun Status",
      subtitle: "Connector state",
      icon: <BatteryCharging size={20} />,
      values: {
        gun1_cp_state: latestRecord.gun1_cp_state,
        gun2_cp_state: latestRecord.gun2_cp_state,
      },
    },
    {
      title: "DC Meter 1",
      subtitle: "Primary output",
      icon: <Zap size={20} />,
      values: {
        dc_meter1_voltage: latestRecord.dc_meter1_voltage,
        dc_meter1_current: latestRecord.dc_meter1_current,
        dc_meter1_power: latestRecord.dc_meter1_power,
        dc_meter1_energy_reading: latestRecord.dc_meter1_energy_reading,
        dc_meter1_energy_consumed: latestRecord.dc_meter1_energy_consumed,
      },
    },
    {
      title: "DC Meter 2",
      subtitle: "Secondary output",
      icon: <Zap size={20} />,
      values: {
        dc_meter2_voltage: latestRecord.dc_meter2_voltage,
        dc_meter2_current: latestRecord.dc_meter2_current,
        dc_meter2_power: latestRecord.dc_meter2_power,
        dc_meter2_energy_reading: latestRecord.dc_meter2_energy_reading,
        dc_meter2_energy_consumed: latestRecord.dc_meter2_energy_consumed,
      },
    },
    {
      title: "Total DC",
      subtitle: "Combined output",
      icon: <Gauge size={20} />,
      values: {
        total_dc_power: latestRecord.total_dc_power,
        total_energy: latestRecord.total_energy,
        total_active_power: latestRecord.total_active_power,
        efficiency: latestRecord.efficiency,
      },
    },
    {
      title: "Phase Readings",
      subtitle: "Input side",
      icon: <Activity size={20} />,
      values: {
        phase1_voltage: latestRecord.phase1_voltage,
        phase1_current: latestRecord.phase1_current,
        phase1_power: latestRecord.phase1_power,
        phase2_voltage: latestRecord.phase2_voltage,
        phase2_current: latestRecord.phase2_current,
        phase2_power: latestRecord.phase2_power,
        phase3_voltage: latestRecord.phase3_voltage,
        phase3_current: latestRecord.phase3_current,
        phase3_power: latestRecord.phase3_power,
        ac_meter_energyreading: latestRecord.ac_meter_energyreading,
      },
    },
    {
      title: "Temperature",
      subtitle: "Cooling loop",
      icon: <Thermometer size={20} />,
      values: {
        gun1_temp: latestRecord.gun1_temperature,
        gun2_temp: latestRecord.gun2_temperature,
        total_avg_temp: latestRecord.guns_avg_temp,
        ambient_temperature: latestRecord.ambient_temperature,
        cabin_temperature: latestRecord.cabin_temperature,
      },
    },
  ];

  return (
    <section className="liquid-panel">
      <div className="liquid-panel__header">
        <div>
          <span className="liquid-eyebrow">
            <Droplets size={16} />
            Liquid cooled telemetry
          </span>
          <h2>Liquid Cooled System Data</h2>
          <p>Live electrical, thermal, and cooling loop readings.</p>
        </div>

        <div className="liquid-live-pill">
          <span />
          Live feed
        </div>
      </div>

      <div className="liquid-summary-grid">
        {summaryMetrics.map((metric) => (
          <article
            key={metric.label}
            className={`liquid-summary-card is-${metric.tone}`}
          >
            <span>{metric.label}</span>
            <strong>{formatValue(metric.value, metric.key)}</strong>
          </article>
        ))}
      </div>

      <div className="liquid-sections">
        {categories.map((category) => (
          <article className="liquid-section-card" key={category.title}>
            <div className="liquid-section-card__header">
              <span className="liquid-section-icon">{category.icon}</span>
              <div>
                <h3>{category.title}</h3>
                <p>{category.subtitle}</p>
              </div>
            </div>

            <div className="liquid-metric-grid">
              {Object.entries(category.values).map(([key, value]) => {
                const isStatus = key.includes("cp_state");

                return (
                  <div
                    key={key}
                    className={`liquid-metric-card ${
                      isStatus ? getStatusTone(value) : ""
                    }`}
                  >
                    <span>{formatLabel(key)}</span>
                    <strong>{formatValue(value, key)}</strong>
                  </div>
                );
              })}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default LiquidCooledDetails;
